"use client";

import { Zap, TrendingUp, Compass, Star, Target } from "lucide-react";
import type { ReportData } from "./reportData";

interface AvatarSnapshotCardProps {
  report: ReportData;
}

export default function AvatarSnapshotCard({ report }: AvatarSnapshotCardProps) {
  const { snapshot } = report;

  return (
    <div
      className="rounded-2xl p-8 md:p-10 relative overflow-hidden fade-in-up"
      style={{
        background: "linear-gradient(135deg, rgba(124,62,97,0.35) 0%, rgba(0,18,88,0.5) 60%, rgba(54,23,64,0.7) 100%)",
        border: "1px solid rgba(219,184,77,0.4)",
        boxShadow: "0 0 60px rgba(219,184,77,0.1), 0 16px 48px rgba(0,0,0,0.4)",
      }}
    >
      {/* Background decoration */}
      <div
        className="absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(219,184,77,0.6) 0%, transparent 70%)",
          transform: "translate(30%, -30%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-48 h-48 opacity-8 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(124,62,97,0.8) 0%, transparent 70%)",
          transform: "translate(-30%, 30%)",
        }}
      />

      {/* Header */}
      <div className="relative mb-8">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-1 h-6 rounded-full"
            style={{ background: "linear-gradient(180deg, #dbb84d, #7c3e61)" }}
          />
          <span
            className="text-xs font-600 tracking-widest uppercase"
            style={{ color: "rgba(219,184,77,0.7)", letterSpacing: "0.12em", fontWeight: 600 }}
          >
            Avatar Snapshot
          </span>
        </div>
        <h2
          className="text-2xl md:text-3xl font-display text-white/95 leading-tight"
          style={{ fontWeight: 700 }}
        >
          At-a-Glance: Who You Are
        </h2>
      </div>

      {/* Primary Archetype — Hero */}
      <div
        className="relative rounded-xl p-6 mb-6"
        style={{
          background: "linear-gradient(135deg, rgba(219,184,77,0.12) 0%, rgba(124,62,97,0.2) 100%)",
          border: "1px solid rgba(219,184,77,0.35)",
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #7c3e61, #dbb84d)",
              boxShadow: "0 4px 16px rgba(219,184,77,0.3)",
            }}
          >
            <Star className="w-6 h-6 text-deep-900" />
          </div>
          <div>
            <p
              className="text-xs font-600 tracking-widest uppercase mb-1"
              style={{ color: "rgba(219,184,77,0.65)", letterSpacing: "0.1em", fontWeight: 600 }}
            >
              Primary Archetype
            </p>
            <p
              className="text-xl md:text-2xl font-display gold-gradient-text leading-tight"
              style={{ fontWeight: 800 }}
            >
              {snapshot.primaryArchetype}
            </p>
          </div>
        </div>
      </div>

      {/* Core Theme */}
      <div
        className="rounded-xl p-5 mb-4"
        style={{
          background: "rgba(0,18,88,0.4)",
          border: "1px solid rgba(219,184,77,0.15)",
        }}
      >
        <div className="flex items-start gap-3">
          <Compass className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "#dbb84d" }} />
          <div>
            <p
              className="text-xs font-600 tracking-wider uppercase mb-1.5"
              style={{ color: "rgba(219,184,77,0.6)", letterSpacing: "0.08em", fontWeight: 600 }}
            >
              Core Theme
            </p>
            <p className="text-sm md:text-base text-white/85 leading-relaxed italic" style={{ fontWeight: 400 }}>
              "{snapshot.coreTheme}"
            </p>
          </div>
        </div>
      </div>

      {/* Key Strength + Growth Edge + Life Direction — Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Key Strength */}
        <div
          className="rounded-xl p-4"
          style={{
            background: "rgba(54,23,64,0.6)",
            border: "1px solid rgba(219,184,77,0.15)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4" style={{ color: "#dbb84d" }} />
            <p
              className="text-xs font-600 tracking-wider uppercase"
              style={{ color: "rgba(219,184,77,0.6)", letterSpacing: "0.08em", fontWeight: 600 }}
            >
              Key Strength
            </p>
          </div>
          <p className="text-sm text-white/80 leading-relaxed" style={{ fontWeight: 500 }}>
            {snapshot.keyStrength}
          </p>
        </div>

        {/* Growth Edge */}
        <div
          className="rounded-xl p-4"
          style={{
            background: "rgba(54,23,64,0.6)",
            border: "1px solid rgba(124,62,97,0.3)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4" style={{ color: "#7c3e61" }} />
            <p
              className="text-xs font-600 tracking-wider uppercase"
              style={{ color: "rgba(124,62,97,0.9)", letterSpacing: "0.08em", fontWeight: 600 }}
            >
              Growth Edge
            </p>
          </div>
          <p className="text-sm text-white/80 leading-relaxed" style={{ fontWeight: 500 }}>
            {snapshot.growthEdge}
          </p>
        </div>

        {/* Life Direction */}
        <div
          className="rounded-xl p-4"
          style={{
            background: "rgba(54,23,64,0.6)",
            border: "1px solid rgba(0,18,88,0.6)",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4" style={{ color: "#dbb84d" }} />
            <p
              className="text-xs font-600 tracking-wider uppercase"
              style={{ color: "rgba(219,184,77,0.6)", letterSpacing: "0.08em", fontWeight: 600 }}
            >
              Life Direction
            </p>
          </div>
          <p className="text-sm text-white/80 leading-relaxed" style={{ fontWeight: 500 }}>
            {snapshot.lifeDirection}
          </p>
        </div>
      </div>
    </div>
  );
}